import { ListData } from './items-list/list-data.service';
import { ILocalSearchDataItemModel, LocalSearchModel } from '@fi-shared/models';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { Directive, Input, OnDestroy } from '@angular/core';
import { isDefined } from '@fi-shared/utils/is-defined';
import { SideListOptionType } from '@fi-shared/types';
import {
    ItemsListElementType,
    ItemsListHeader,
    ItemsListHeaderProperties,
} from '@fi-app/components/items-list/items-list-header';
import { IListViewTag, ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { interval, Subject } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';

export interface IItemsListParams {
    WildcardSearch?: string;
    ShowCheckBox?: boolean;
    CanShowLoadMore?: boolean;
    noLocalSearch?: boolean;
    initialSideListOptionType?: SideListOptionType;
    showDelete?: boolean;
    initialSearchObjects?: ILocalSearchDataItemModel[];
    defaultFilterTypeFn?: () => SideListOptionType;
}

@Directive()
export abstract class ItemsListContainerBase<T extends ListData<any>> implements OnDestroy {
    @Input('listData') ListData: T;

    get selectedItems$() {
        return this.ListData.selectedItems$;
    }

    protected WildcardSearch = '';

    protected onInit(listAccessor: () => T, params?: IItemsListParams) {
        if (!this.ListData) this.ListData = listAccessor();

        if (!params) params = {};

        if (isDefined(params.WildcardSearch)) this.WildcardSearch = params.WildcardSearch;

        if (isDefined(params.showDelete)) this.ListData.ShowDelete = params.showDelete;

        if (isDefined(params.ShowCheckBox)) this.ListData.ShowCheckBox = params.ShowCheckBox;

        if (isDefined(params.CanShowLoadMore)) this.ListData.ShowCheckBox = params.CanShowLoadMore;

        if (isDefined(params.initialSideListOptionType)) this.ListData.FilterType = params.initialSideListOptionType;

        if (this.ListData.Statefull) {
            this.ListData.setFromStateFilterType();
            this.ListData.setFromStateSelectedTags();
        }

        this.ListData.loadDataFunc = this.LoadItems.bind(this);
        this.ListData.loadCountFunc = this.SetItemsCount.bind(this);
        if (!params.noLocalSearch) {
            this.ListData.isV2SearchEnabled = true;
            this.ListData.localSearchFunc = () => this.InitializeLocalSearchData();
        }
        this.ListData.Initialize(params.initialSearchObjects);

        this.ListData.SelectTag = (tag: IListViewTag, skipUpdatingSelectedTags: boolean) => {
            let selectedTagIds = this.ListData.selectedTags;
            // if we choose tags from left side ListData.selectedTags has correct value already
            // if we choose tags from top then we should manually update selectedTags
            // https://chainformation.atlassian.net/browse/FI-4595
            if (!skipUpdatingSelectedTags) {
                selectedTagIds = this.ListData.isTagSelected(tag) ? undefined : [tag.Id];
            }
            this.SetFilterType(params.defaultFilterTypeFn(), selectedTagIds);
        };

        let processingLocalSearchTextChanged = false;
        this.ListData.localSearchTextChanged$
            .pipe(takeUntil(this.ListData.ngOnDestroy$))
            .pipe(debounce(() => interval(500)))
            .subscribe((searchText) => {
                if (processingLocalSearchTextChanged || searchText.length < 3) return;
                processingLocalSearchTextChanged = true;
                this.onLocalSearchTextChanged(searchText).finally(() => (processingLocalSearchTextChanged = false));
            });
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ListData.CancelRequests();
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    getSelectedFilterSideList(): number[] {
        /*if (!!this.ListData.selectedTags.length) {
            return [];
        }*/
        // todo can we choose simultaneously filter type and tags?
        return [this.ListData.FilterType];
    }

    selectTagOption(tagId: number): void {
        const selectedTag: IListViewTag = this.ListData.ListViewSettings.ListViewTags.find((tag: IListViewTag) => {
            return tag.Id === tagId;
        });
        if (isDefined(selectedTag)) {
            this.ListData.SelectTag(selectedTag, true);
        } else {
            this.SetFilterType(this.ListData.FilterType);
        }
    }

    getSideListOptionsTags(): ISideListOptionsItem[] {
        return this.ListData.ListViewSettings.ListViewTags.map((tag: IListViewTag) => {
            const result: ISideListOptionsItem = {
                value: tag.Id,
                text: tag.Name,
                Level: tag.Level,
                ParentId: tag.ParentId,
                IsDisabled: tag.IsDisabled,
            };
            return result;
        });
    }

    protected abstract LoadItems(): Promise<void>;
    protected SetItemsCount(): IFiHttpPromise<number> {
        return undefined;
    }
    protected InitializeLocalSearchData(): Promise<void> {
        return undefined;
    }

    protected onLocalSearchTextChanged(searchText: string) {
        return Promise.resolve();
    }

    IsSideItemSelected(filterType) {
        return this.ListData.FilterType.toString() === filterType.toString();
    }

    IsTagSelected(tagId) {
        return this.ListData.selectedTags && this.ListData.selectedTags.indexOf(tagId) >= 0;
    }

    SideListItemClick(filterType: SideListOptionType) {
        this.SetFilterType(isDefined(filterType) ? filterType : SideListOptionType.SharedWithMe); // todo should we use SharedWithMe for Users list?
    }

    SetFilterType(filterType: SideListOptionType, selectedTagIds?: number[], preventSearch?: boolean) {
        this.ListData.setFilterType(filterType);
        this.ListData.selectedTags = selectedTagIds || [];
        if (this.ListData.isV2SearchEnabled && !preventSearch) {
            this.ListData.SearchFunction();
        }
        this.toggleSuperAdminColumn();
    }

    //#region Super admin routine
    isSuperAdminIconShown(sideListItem): boolean {
        return sideListItem.Id === SideListOptionType.All;
    }

    protected checkApplySuperAdminRight(
        isSuperAdminRight: boolean,
        sideListOptions: ISideListOptionsItem[],
        text: string,
    ) {
        if (isSuperAdminRight) {
            this.hasSuperAdminRight = true;
            sideListOptions.unshift({
                value: SideListOptionType.All,
                text: text,
            });

            this.toggleSuperAdminColumn();
        }
    }

    private hasSuperAdminRight = false;
    private IsSuperAdminColumnAdded: boolean = false;
    isSuperAdminAccess: boolean;
    private superAdminColumn = new ItemsListHeader(
        '',
        'HasViewAccessAsSuperAdmin',
        new ItemsListHeaderProperties(
            'CcmListItem-Icon SuperAdminIcon CcmListItem-NoBorder',
            true,
            ItemsListElementType.SpanPlain,
            {
                getMainValueClass: (data) => (data && data.HasViewAccessAsSuperAdmin ? 'far fa-shield-check' : ''),
            },
        ),
        undefined,
        undefined,
        ' ',
    );

    protected toggleSuperAdminColumn(): void {
        if (!this.hasSuperAdminRight) return;
        const showSuperAdminColumn: boolean =
            this.ListData.FilterType === SideListOptionType.All || this.isSuperAdminAccess; // new document filter support
        if (showSuperAdminColumn && !this.IsSuperAdminColumnAdded) {
            const nameColumnIndex: number = this.ListData.Header.findIndex((header: ItemsListHeader) => {
                return header.PropertyName === 'Name';
            });
            const startIndex: number = nameColumnIndex - 1 < 0 ? 0 : nameColumnIndex - 1;
            this.ListData.Header.splice(startIndex, 0, this.superAdminColumn);
            this.IsSuperAdminColumnAdded = true;
        } else if (!showSuperAdminColumn && this.IsSuperAdminColumnAdded) {
            const superAdminColumnIndex: number = this.ListData.Header.findIndex((header: ItemsListHeader) => {
                return header.PropertyName === 'HasViewAccessAsSuperAdmin';
            });
            this.ListData.Header.splice(superAdminColumnIndex, 1);
            this.IsSuperAdminColumnAdded = false;
        }
    }
    //#endregion
}
